/** @jsx jsx */
/** @jsxFrag React.Fragment */
import { jsx } from '@emotion/react';

import * as React from 'react';

import { FormattedMessage } from 'js/lib/coursera.react-intl';

import type { Theme } from '@coursera/cds-core';
import { Typography2, breakpoints, useTheme } from '@coursera/cds-core';

import EnrollmentChoiceModalOptionListContainer from 'bundles/enterprise-legacy-learner-home/components/EnrollmentChoiceModalOptionListContainer';

import _t from 'i18n!nls/enterprise-legacy-learner-home';

type PropsFromCaller = {
  productType: string;
  orgOptions: OrganizationOption[];
  orgId?: string;
  onChangeRadio: (event: React.ChangeEvent<HTMLInputElement>) => void;
};

type OrganizationOption = {
  id: string;
  name: string;
};

type Props = PropsFromCaller;

const styles = {
  orgListItem: (theme: Theme) => ({
    display: 'flex',
    flexFlow: 'row nowrap',
    margin: 'var(--cds-spacing-200) 0',
    [breakpoints.down('xs')]: {
      margin: 'var(--cds-spacing-200) 0',
    },
  }),
  orgListItemRadio: {
    // Global CSS messes with this, so punch it with !important
    margin: '0 0 0 0 !important',
    zoom: 2,
    flexShrink: 0,
  },
  orgListItemLabel: (theme: Theme) => ({
    margin: '0 0 0 var(--cds-spacing-150)',
    flexGrow: 1,
  }),
};

const EnrollmentChoiceModalOrganizationList = ({ productType, orgOptions, orgId, onChangeRadio }: Props) => {
  const theme = useTheme();

  // ---
  return (
    <EnrollmentChoiceModalOptionListContainer
      title={_t('Select an organization:')}
      isMultiChoice={true}
      description={
        <FormattedMessage
          message={_t(
            'This {productType, select, course {course} s12n {specialization}} is available in {orgCount, plural, =1 {# organization} other {# organizations}} that you belong to. Your {productType, select, course {course} s12n {specialization}} will be tracked in the organization you select.'
          )}
          productType={productType}
          orgCount={orgOptions.length}
        />
      }
    >
      <div id="EnrollmentChoiceModal-GroupLabel" className="sr-only">
        {_t('Organization')}
      </div>
      {orgOptions.map((org) => {
        const htmlId = `ecm~${org.id}`;
        return (
          <div css={styles.orgListItem(theme)} key={org.id}>
            <input
              css={styles.orgListItemRadio}
              type="radio"
              id={htmlId}
              value={org.id}
              checked={org.id === orgId}
              onChange={onChangeRadio}
            />
            <label css={styles.orgListItemLabel(theme)} htmlFor={htmlId}>
              <Typography2 component="span">{org.name}</Typography2>
            </label>
          </div>
        );
      })}
    </EnrollmentChoiceModalOptionListContainer>
  );
};

export default EnrollmentChoiceModalOrganizationList;
